import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Store, Star, ShieldCheck, Crown, Loader2, Quote, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { usePageMeta } from "@/hooks/usePageMeta";

function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "text-[#E8956B]" : "text-neutral-300"} fill={n <= Math.round(value) ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

export default function SellerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/sellers/${id}/profile`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [id]);

  usePageMeta(
    data && data.name ? `${data.name} — Seller Profile | Beauty Trade Club` : "Seller Profile | Beauty Trade Club",
    data && data.name ? `Shop ${data.name}'s listings and read verified buyer reviews on Beauty Trade Club.` : "",
    `/seller/${id}`
  );

  if (data === null) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#E8956B]" /></div>;
  if (data === false) return <div className="text-center py-32 text-neutral-400" data-testid="seller-not-found">Seller not found.</div>;

  const initial = data.name?.[0]?.toUpperCase() || "S";

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="seller-profile">
      {/* HEADER */}
      <div className="bg-white border border-neutral-200 rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6">
        <div className="w-24 h-24 rounded-full bg-[#E8956B] text-white flex items-center justify-center text-4xl font-black shrink-0">
          {initial}
        </div>
        <div className="text-center sm:text-left flex-1">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <h1 className="font-display text-3xl font-black" data-testid="seller-name">{data.name}</h1>
            {data.is_vip && <span className="inline-flex items-center gap-1 bg-black text-white text-[10px] font-bold px-2 py-1 rounded-full"><Crown size={11} className="text-[#E8956B]" fill="currentColor" /> VIP</span>}
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-2 mt-1 text-sm text-[#E8956B] font-semibold">
            <ShieldCheck size={15} /> Verified Seller
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-4 mt-3 text-sm text-neutral-600 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Stars value={data.rating} />
              {data.review_count > 0 ? <b className="text-neutral-900">{data.rating}</b> : <span className="text-neutral-400">No reviews yet</span>}
              {data.review_count > 0 && <span className="text-neutral-400">({data.review_count})</span>}
            </span>
            <span className="flex items-center gap-1.5"><Store size={14} className="text-[#E8956B]" /> {data.product_count} listing{data.product_count === 1 ? "" : "s"}</span>
            {data.trade_review_count > 0 && (
              <span data-testid="profile-trade-rating" className="flex items-center gap-1.5">
                <Repeat size={14} className="text-[#E8956B]" />
                <b className="text-neutral-900">{data.trade_rating}</b>
                <span className="text-neutral-400">({data.trade_review_count} trade{data.trade_review_count === 1 ? "" : "s"})</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* LISTINGS */}
      <h2 className="font-display text-2xl font-bold mt-12 mb-6">Listings</h2>
      {data.products.length === 0 ? (
        <p className="text-neutral-400" data-testid="seller-no-listings">This seller has no active listings right now.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="seller-listings">
          {data.products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}

      {/* REVIEWS */}
      <h2 className="font-display text-2xl font-bold mt-14 mb-6">Buyer Reviews {data.review_count > 0 && <span className="text-neutral-400 text-lg">({data.review_count})</span>}</h2>
      {data.reviews.length === 0 ? (
        <p className="text-neutral-400" data-testid="seller-no-reviews">No reviews yet — be the first to buy and rate this seller!</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="seller-reviews">
          {data.reviews.map((r) => (
            <div key={r.id} className="bg-white border border-neutral-200 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-neutral-900">{r.buyer_name}</span>
                <Stars value={r.rating} size={15} />
              </div>
              {r.comment && (
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex gap-1.5">
                  <Quote size={14} className="text-[#E8956B] shrink-0 mt-0.5" /> {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TRADE REVIEWS */}
      {data.trade_review_count > 0 && (
        <>
          <h2 className="font-display text-2xl font-bold mt-14 mb-6 flex items-center gap-2">
            <Repeat size={22} className="text-[#E8956B]" /> Trade Reviews <span className="text-neutral-400 text-lg">({data.trade_review_count})</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="seller-trade-reviews">
            {data.trade_reviews.map((r) => (
              <div key={r.id} className="bg-white border border-neutral-200 rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-neutral-900">{r.reviewer_name}</span>
                  <Stars value={r.rating} size={15} />
                </div>
                {r.comment && (
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex gap-1.5">
                    <Quote size={14} className="text-[#E8956B] shrink-0 mt-0.5" /> {r.comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
